import { queryOptions } from "@tanstack/react-query"
import { isAxiosError } from "axios"
import { client } from "./client"
import { safeQueryOptions } from "@src/utils/query"
import { UnpackPromise, UnpackApiList, UnpackArray } from "@src/utils/types"
import { components } from "./schema"
import { ApiDynamicFilter } from "./filters"
import { FormDetailDto } from "./form"
import { useAppStore } from "@src/state"

export enum UserInvitationPolicy {
  Undefined = 0,
  Anyone = 1,
  OnlyPlatformUsers = 2,
  OnlyCommunityUsers = 3,
  None = 4, // only community admins can invite
}

export enum UserJoinCommunitySetting {
  JoinWithoutApproval = 0,
  JoinWithApproval = 1,
  JoinWithInvitation = 2,
}

export enum TaskUnlock {
  Calendar = 0,
  Freestyle = 1,
  Sequential = 2,
  Stagegate = 3,
}

export enum DevelopmentStageType {
  None = 0,
  Default = 1,
  CustomType = 2,
}

export enum CommunityType {
  Public = 0,
  Private = 1,
  Hybrid = 2,
}

export enum CommunityUserSortBy {
  None,
  Name,
  Comments,
  Likes,
  Followers,
  Activity,
  RegisteredNewest = 10,
}

export type CommunityUserFilter = {
  skip?: number
  take?: number
  communityId?: number
  communityCircleId?: number | null
  circleFilter?: ApiDynamicFilter<number>
  name?: string | null
  orderBy?: CommunityUserSortBy
  countryId?: number | null
  countryFilter?: ApiDynamicFilter<number>
  interestId?: number | null
  industryFilter?: ApiDynamicFilter<number>
  skillId?: number | null
  skillFilter?: ApiDynamicFilter<number>
  sustainableDevelopmentGoalId?: number | null
  marketExpertiseId?: number | null
  userMarketsExpertisesFilter?: ApiDynamicFilter<number>
  tags?: string[] | null
  tagFilter?: ApiDynamicFilter<string>
  companyFilter?: ApiDynamicFilter<string>
  userMethodologyFilter?: ApiDynamicFilter<number>
}

export type CommunityCommentFilter =
  components["schemas"]["CommunityCommentFilter"]

export type CommunityCommentList =
  components["schemas"]["CommunityCommentListDto"]

export const communityKeys = {
  all: ["community"],
  list: () => [...communityKeys.all, "list"],
  overview: (id?: number) => [...communityKeys.all, id, "overview"],
  about: (id?: number) => [...communityKeys.all, id, "about"],
  users: (filters: CommunityUserFilter) => [
    ...communityKeys.all,
    "users",
    filters,
  ],
  userMapPins: (filters: CommunityUserFilter) => [
    ...communityKeys.all,
    "userMapPins",
    filters,
  ],
  userPending: (id?: number) => [...communityKeys.all, id, "userPending"],
  projectsIBelongAdminCircle: (id?: number) => [
    ...communityKeys.all,
    id,
    "projectsIBelongAdminCircle",
  ],
  comments: (filters: Omit<CommunityCommentFilter, "skip">) => [
    ...communityKeys.all,
    "comments",
    filters,
  ],
  projectsPendingWork: (communityId?: number) => [
    ...communityKeys.all,
    communityId,
    "projectsPendingWork",
  ],
  awaitingFeedbackProjects: (communityId?: number) => [
    ...communityKeys.all,
    communityId,
    "awaitingFeedbackProjects",
  ],
  pendingProjectsCount: (communityId?: number) => [
    ...communityKeys.all,
    communityId,
    "pendingProjectsCount",
  ],
  customDns: (communityId?: number) => [
    ...communityKeys.all,
    communityId,
    "customDns",
  ],
  projectForm: (id?: number) => [...communityKeys.all, id, "projectForm"],
  inviteLinks: (communityId?: number) => [
    ...communityKeys.all,
    communityId,
    "inviteLinks",
  ],
  tabVisibility: (communityId?: number) => [
    ...communityKeys.all,
    communityId,
    "tabVisibility",
  ],
}

export type CommunityOverview = UnpackPromise<
  ReturnType<typeof getCommunityOverview>
>

export type CommunityEditInfo = components["schemas"]["CommunityEditInfoDto"]
export type SaveCommunityInfoResponse = UnpackPromise<
  ReturnType<typeof saveCommunityInfo>
>
export type CommunityUserList = UnpackPromise<
  ReturnType<typeof getCommunityUsers>
>
export type CommunityUser = UnpackArray<
  UnpackApiList<CommunityUserList["users"]>
>
export type SaveCommunityAboutResponse = UnpackPromise<
  ReturnType<typeof saveCommunityAbout>
>
export type CommunityAbout = components["schemas"]["CommunityAboutDto"]

export const getCommunityOverview = async (id: number) => {
  const data = await client.GET("/api/Community/GetOverview", {
    params: {
      query: { id },
    },
  })

  // Can't really figure out a better way to handle this right now. This is temporary until the old frontend is fully deprecated.
  const oldFrontendCustomDns = data?.customDns?.host || ""
  localStorage.setItem("oldFrontendCustomDns", oldFrontendCustomDns)

  // `tabOrder` is augmented here until schema.ts is regenerated from swagger.
  return data as typeof data & { tabOrder?: string | null }
}

export const saveCommunityTabOrder = async (
  communityId: number,
  tabOrder: string,
) => {
  // Bypasses typed openapi client until schema.ts is regenerated from swagger.
  await client.axiosInstance.post("/api/Community/SaveTabOrder", {
    communityId,
    tabOrder,
  })
}

export const isCommunityAccessDeniedError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 403

export const getCommunityOverviewQuery = (id?: number) =>
  safeQueryOptions({
    queryFn: getCommunityOverview,
    queryKeys: communityKeys.overview(id),
    unsafeQueryFnArgs: [id],
  })

export const getCommunityAbout = async (id: number) => {
  const data = await client.GET("/api/Community/GetAbout", {
    params: {
      query: { id },
    },
  })

  useAppStore.getState().setCustomDnsHost(data?.customDnsHost || "")

  return data
}

export const getCommunityAboutQuery = (id?: number) =>
  safeQueryOptions({
    queryFn: getCommunityAbout,
    queryKeys: communityKeys.about(id),
    unsafeQueryFnArgs: [id],
  })

export const saveCommunityInfo = async (
  communityEditInfo: CommunityEditInfo,
) => {
  const data = await client.POST("/api/Community/SaveInfo", {
    body: communityEditInfo,
  })

  return data
}

export const getCommunityUsers = async (filters: CommunityUserFilter) => {
  const data = await client.POST("/api/Community/GetUserList", {
    body: filters,
  })

  return data
}

export const getCommunityUsersQuery = (filters?: CommunityUserFilter) =>
  safeQueryOptions({
    queryFn: getCommunityUsers,
    queryKeys: communityKeys.users(filters || {}),
    unsafeQueryFnArgs: [filters],
  })

export type UserMapPinDto = components["schemas"]["UserMapPinDto"]

export const getCommunityUserMapPins = async (filter: CommunityUserFilter) => {
  const data = await client.POST("/api/Community/GetUserMapPins", {
    body: filter,
  })

  return (data || []) as UserMapPinDto[]
}

export const getCommunityUserMapPinsQuery = (filter?: CommunityUserFilter) =>
  safeQueryOptions({
    queryFn: getCommunityUserMapPins,
    queryKeys: communityKeys.userMapPins(filter || {}),
    unsafeQueryFnArgs: [filter],
  })

export const getCommunityUserPending = async (communityId: number) => {
  const data = await client.GET("/api/Community/UserPending/{communityId}", {
    params: {
      path: { communityId },
    },
  })

  return data
}

export const getCommunityUserPendingQuery = (communityId?: number) =>
  safeQueryOptions({
    queryFn: getCommunityUserPending,
    queryKeys: communityKeys.userPending(communityId),
    unsafeQueryFnArgs: [communityId],
  })

export const acceptProject = async (communityId: number, projectId: number) => {
  await client.GET("/api/Community/AcceptProject", {
    params: {
      query: {
        communityId,
        projectId,
      },
    },
  })
}

export const denyProject = async (communityId: number, projectId: number) => {
  await client.GET("/api/Community/DenyProject", {
    params: {
      query: {
        communityId,
        projectId,
      },
    },
  })
}

export type CommunityUserListDto = components["schemas"]["CommunityUserListDto"]
export type CommunityUserForListDto =
  components["schemas"]["CommunityUserForListDto"]

export type CommunityEditAbout = components["schemas"]["CommunityEditAboutDto"]

export const saveCommunityAbout = async (aboutInfo: CommunityEditAbout) => {
  const data = await client.POST("/api/Community/SaveAbout", {
    body: aboutInfo,
  })

  return data
}

export const getCommunityProjectForm = async (id: number) => {
  const data = await client.GET("/api/Community/{id}/ProjectForm", {
    params: {
      path: { id },
    },
  })

  return data
}

export const getCommunityProjectFormQuery = (id?: number) =>
  safeQueryOptions({
    queryFn: getCommunityProjectForm,
    queryKeys: communityKeys.projectForm(id),
    unsafeQueryFnArgs: [id],
  })

// Performing type gymnastics to reconciliate the editor preview form with the backend
// form type :weary:
export const saveCommunityProjectForm = async (
  body: Omit<FormDetailDto, "userCreatorId"> & {
    userCreatorId?: number | null
  },
  id: number,
) => {
  const data = await client.PUT("/api/Community/{id}/ProjectForm", {
    params: {
      path: { id },
    },
    body,
  })

  return data
}

export const acceptCommunityInvitation = async (
  communityId: number,
  userId: number,
) => {
  await client.GET("/api/Community/AcceptInvitation", {
    params: {
      query: { communityId, userId },
    },
  })
}

export const declineCommunityInvitation = async (
  communityId: number,
  userId: number,
) => {
  await client.GET("/api/Community/DeclineInvitation", {
    params: {
      query: { communityId, userId },
    },
  })
}

export const removeCommunityInvitation = async (
  communityId: number,
  userId: number,
  email?: string,
) => {
  await client.GET("/api/Community/RemoveInvitation", {
    params: {
      query: {
        communityId,
        userId,
        email,
      },
    },
  })
}

export const approveCommunityJoinRequest = async (
  communityId: number,
  userId: number,
) => {
  await client.GET("/api/Community/ApproveJoinRequest", {
    params: {
      query: {
        communityId,
        userId,
      },
    },
  })
}

export const denyCommunityJoinRequest = async (
  communityId: number,
  userId: number,
) => {
  await client.GET("/api/Community/DenyJoinRequest", {
    params: {
      query: {
        communityId,
        userId,
      },
    },
  })
}

export const getCommunityProjectsIBelongAdminCircle = async (
  communityId: number,
) => {
  const data = await client.GET(
    "/api/Community/{communityId}/projects-i-belong-admin-circle",
    {
      params: {
        path: { communityId },
      },
    },
  )

  return data
}

export const getCommunityProjectsIBelongAdminCircleQuery = (
  communityId?: number,
) =>
  safeQueryOptions({
    queryFn: getCommunityProjectsIBelongAdminCircle,
    queryKeys: communityKeys.projectsIBelongAdminCircle(communityId),
    unsafeQueryFnArgs: [communityId],
  })

export const getCommunityComments = async (filters: CommunityCommentFilter) => {
  const data = await client.POST("/api/Community/GetCommentList", {
    body: filters,
  })

  return data
}

export const getCommunityCommentsQuery = (filters: CommunityCommentFilter) =>
  safeQueryOptions({
    queryFn: getCommunityComments,
    queryKeys: communityKeys.comments(filters),
    unsafeQueryFnArgs: [filters],
  })

export type ProjectPendingWork = Omit<
  components["schemas"]["ProjectPendingWorkDto"],
  "nextActivity"
> & {
  nextActivity?: components["schemas"]["NextActivityDto"] | null
}

export const getProjectsPendingWork = async (communityId: number) => {
  const data = await client.GET(
    "/api/Community/{communityId}/ProjectsPendingWork",
    {
      params: {
        path: { communityId },
      },
    },
  )

  return data
}

export const getProjectsPendingWorkQuery = (communityId?: number) =>
  safeQueryOptions({
    queryFn: getProjectsPendingWork,
    queryKeys: communityKeys.projectsPendingWork(communityId),
    unsafeQueryFnArgs: [communityId],
  })

export const getCommunityAwaitingFeedbackProjects = async (
  communityId: number,
) => {
  const data = await client.GET("/api/Community/AwaitingFeedbackProjects", {
    params: {
      query: { communityId },
    },
  })

  return data
}

export const getCommunityAwaitingFeedbackProjectsQuery = (
  communityId?: number,
) =>
  safeQueryOptions({
    queryFn: getCommunityAwaitingFeedbackProjects,
    queryKeys: communityKeys.awaitingFeedbackProjects(communityId),
    unsafeQueryFnArgs: [communityId],
  })

export const getCommunityPendingProjectsCount = async (communityId: number) => {
  const data = await client.GET("/api/Community/PendingProjectsCount", {
    params: {
      query: { communityId },
    },
  })

  return data
}

export const getCommunityPendingProjectsCountQuery = (communityId?: number) =>
  safeQueryOptions({
    queryFn: getCommunityPendingProjectsCount,
    queryKeys: communityKeys.pendingProjectsCount(communityId),
    unsafeQueryFnArgs: [communityId],
  })

// Admin functions
export type Community = components["schemas"]["Community"]
export type SaveCommunityDto = components["schemas"]["SaveCommunityDto"]
export type CustomDnsDetailDto = components["schemas"]["CustomDnsDetailDto"]

export const getAllCommunities = async () => {
  const data = await client.GET("/api/Community/GetAll")
  return data
}

export type CommunitiesList = Awaited<ReturnType<typeof getAllCommunities>>

export const getAllCommunitiesQuery = () =>
  queryOptions({
    queryKey: communityKeys.list(),
    queryFn: getAllCommunities,
  })

export const saveCommunity = async (community: SaveCommunityDto) => {
  await client.POST("/api/Community/Save", {
    body: community,
  })
}

export const removeCommunity = async (id: number) => {
  await client.GET("/api/Community/Remove", {
    params: {
      query: {
        id,
      },
    },
  })
}

export const getCustomDns = async (communityId: number) => {
  const data = await client.GET("/api/Community/{id}/CustomDns", {
    params: {
      path: {
        id: communityId,
      },
    },
  })
  return data
}

export const getCustomDnsQuery = (communityId?: number) =>
  safeQueryOptions({
    queryKeys: communityKeys.customDns(communityId),
    queryFn: getCustomDns,
    unsafeQueryFnArgs: [communityId],
  })

export type CustomDnsFormData = components["schemas"]["CustomDnsDto"]

export const createCustomDns = async (data: CustomDnsFormData) => {
  const response = await client.POST("/api/CustomDns", {
    body: data,
  })
  return response as CustomDnsDetailDto
}

export const updateCustomDns = async (id: number, data: CustomDnsFormData) => {
  const response = await client.PUT("/api/CustomDns/{id}", {
    params: {
      path: { id },
    },
    body: data,
  })
  return response as CustomDnsDetailDto
}

export const deleteCustomDns = async (id: number) => {
  await client.DELETE("/api/CustomDns/{id}", {
    params: {
      path: {
        id,
      },
    },
  })
}

export type CommunityInviteLinkDto =
  components["schemas"]["CommunityInviteLinkDto"]
export type CommunityInviteLinkCreateDto =
  components["schemas"]["CommunityInviteLinkCreateDto"]
export type CommunityInviteLink = components["schemas"]["CommunityInviteLink"]

export const getCommunityInviteLinks = async (communityId: number) => {
  const data = await client.GET("/api/Community/{id}/InviteLinks", {
    params: {
      path: { id: communityId },
    },
  })
  return data
}

export const getCommunityInviteLinksQuery = (communityId?: number) =>
  safeQueryOptions({
    queryFn: getCommunityInviteLinks,
    queryKeys: communityKeys.inviteLinks(communityId),
    unsafeQueryFnArgs: [communityId],
  })

export const createCommunityInviteLink = async (
  communityId: number,
  body: CommunityInviteLinkCreateDto,
) => {
  const data = await client.POST("/api/Community/{id}/InviteLink", {
    params: {
      path: { id: communityId },
    },
    body,
  })
  return data
}

export const updateCommunityInviteLink = async (
  communityId: number,
  body: CommunityInviteLinkDto,
) => {
  await client.PUT("/api/Community/{id}/InviteLink", {
    params: {
      path: { id: String(communityId) },
    },
    body,
  })
}

export const deleteCommunityInviteLink = async (
  communityId: number,
  inviteId: number,
) => {
  await client.DELETE("/api/Community/{id}/InviteLink/{inviteId}", {
    params: {
      path: { id: String(communityId), inviteId },
    },
  })
}

// Which community menu tabs each circle sees (BAB-460). Only the hidden
// (circle, tab) pairs are stored: any pair not listed is visible. Menu only:
// a hidden tab's page stays reachable by URL.
export type CommunityCircleHiddenTab =
  components["schemas"]["CommunityCircleHiddenTabDto"]
export type SaveCommunityTabVisibility =
  components["schemas"]["SaveCommunityTabVisibilityDto"]

export const getCommunityTabVisibility = async (communityId: number) => {
  const data = await client.GET("/api/Community/{communityId}/TabVisibility", {
    params: {
      path: { communityId },
    },
  })
  return data
}

export const getCommunityTabVisibilityQuery = (communityId?: number) =>
  safeQueryOptions({
    queryFn: getCommunityTabVisibility,
    queryKeys: communityKeys.tabVisibility(communityId),
    unsafeQueryFnArgs: [communityId],
  })

export const saveCommunityTabVisibility = async (
  communityId: number,
  body: SaveCommunityTabVisibility,
) => {
  await client.PUT("/api/Community/{communityId}/TabVisibility", {
    params: {
      path: { communityId },
    },
    body,
  })
}
